import { BaseMeiucaService } from './MeiucaService'
import { env, apiEndpoints } from '@/lib/env'
import type { PaginatedResponse } from './pagination'

// ---------------------------------------------------------------------------
// Types — mirror the `mobile-app-api/v1/me/training-center-plans` DTOs
// ---------------------------------------------------------------------------

export type PlanStatus = 'active' | 'inactive'
export type PaymentOptionType = 'manual' | 'pix_online'

export interface PlanTrainingCenterSummary {
  id: string
  name: string
}

/** Whose plan this is when the account acts for more than one student (guardian scope). */
export interface PlanStudentCustomer {
  id: string
  name: string
}

export interface PlanListItem {
  id: string
  trainingCenterCustomerId: string
  status: PlanStatus
  amountMonthlyCents: number
  startAt: string
  endAt?: string | null
  disabledAt?: string | null
  paymentOptionType: PaymentOptionType
  /** Pending monthly charges count; >0 drives the "a pagar" badge on the card. */
  outstandingCount: number
  trainingCenter: PlanTrainingCenterSummary
  /** Present when the account has dependents (guardian scope). Additive. */
  studentCustomer?: PlanStudentCustomer | null
  isDependent?: boolean | null
}

/** Detail shares the list item's shape plus the open charges, embedded so the
 *  plan detail loads in a single request (the dedicated endpoint stays for polling).
 *  Optional/tolerant: an older backend without the embed → undefined → `?? []`. */
export interface PlanDetailItem extends PlanListItem {
  outstandingPayments?: OutstandingPaymentItem[]
}

/** One student of the account in one center, with where they stand on that
 *  center's general acceptance term (`GET me/training-center-terms`). */
export interface TrainingCenterTermsItem {
  trainingCenter: { id: string; name: string }
  studentCustomer: { id: string; name: string }
  isDependent: boolean
  terms: PlanTerms
}

export interface PlanTerms {
  /** The center's opt-in switch. Off: never ask for a signature; history stays. */
  isEnabled: boolean
  status: 'pending' | 'outdated' | 'accepted'
  publicToken: string
  acceptedAt?: string | null
  lastAcceptedAt?: string | null
  signerDisplayName: string
  isSignedByGuardian: boolean
  history: PlanTermsHistoryEntry[]
}

export interface PlanTermsHistoryEntry {
  id: string
  kind: 'general' | 'contract'
  acceptedAt: string
  signerDisplayName: string
  templateVersion: number
  /** Public page token where this acceptance opens: `/terms/<receiptToken>?a=<id>`. */
  receiptToken: string
  isCurrent: boolean
}

export type MonthlyChargeStatus = 'paid' | 'pending' | 'failed'

export interface MonthlyChargeManualDetails {
  pixKey: string
  pixKeyType: string
}

export interface MonthlyChargeItem {
  id: string
  yearMonth: string
  startAt: string
  endAt?: string | null
  amountCents: number
  status: MonthlyChargeStatus
  paymentDetails?: {
    type: PaymentOptionType
    manual?: MonthlyChargeManualDetails | null
  } | null
}

export interface OutstandingPixInfo {
  payload: string
  encodedImage: string
  expirationDate?: string | null
  invoiceUrl?: string | null
}

export interface OutstandingManualInfo {
  pixKey: string
  pixKeyType: string
}

/** An open monthly charge, already carrying what the payer needs to pay it. */
export interface OutstandingPaymentItem {
  id: string
  yearMonth: string
  amountCents: number
  dueAt: string
  paymentType: PaymentOptionType
  pix?: OutstandingPixInfo | null
  manual?: OutstandingManualInfo | null
}

export interface CompletedLessonItem {
  id: string
  occurredAt: string
  startTimeMin: number
  endTimeMin: number
  locationName: string
  trainingClassDescription?: string | null
}

class TrainingCenterPlanService extends BaseMeiucaService {
  constructor() {
    super({
      baseURL: env.API_URL,
      refreshEndpoint: apiEndpoints.auth.refresh,
      cookiePrefix: '',
      debug: env.IS_DEVELOPMENT,
      onAuthenticationError: () => {
        if (typeof window !== 'undefined') {
          window.location.replace('/')
        }
      }
    })
  }

  async listPlans(params?: { status?: PlanStatus }): Promise<PlanListItem[]> {
    const queryParams = new URLSearchParams()
    if (params?.status) queryParams.append('status', params.status)
    const suffix = queryParams.toString() ? `?${queryParams.toString()}` : ''

    // Not paginated — wrapped in the standard `{ data: ... }` envelope.
    const response = await this.get<{ data: PlanListItem[] }>(`/me/training-center-plans${suffix}`)
    return response.data.data
  }

  async getPlanDetail(id: string): Promise<PlanDetailItem> {
    const response = await this.get<{ data: PlanDetailItem }>(
      `/me/training-center-plans/${encodeURIComponent(id)}`
    )
    return response.data.data
  }

  async listMonthlyCharges(
    id: string,
    params?: { limit?: number; cursor?: string }
  ): Promise<PaginatedResponse<MonthlyChargeItem>> {
    const queryParams = new URLSearchParams()
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.cursor) queryParams.append('cursor', params.cursor)
    const suffix = queryParams.toString() ? `?${queryParams.toString()}` : ''

    const response = await this.get<PaginatedResponse<MonthlyChargeItem>>(
      `/me/training-center-plans/${encodeURIComponent(id)}/monthly-charges${suffix}`
    )
    return response.data
  }

  /**
   * Open monthly charges of the plan (cycle still pending), each already
   * carrying the Pix (online) or the static Pix key (manual). Rendered and
   * polled directly by the plan detail — no second per-charge request.
   */
  async listOutstandingPayments(planId: string): Promise<OutstandingPaymentItem[]> {
    const response = await this.get<{ data: OutstandingPaymentItem[] }>(
      `/me/training-center-plans/${encodeURIComponent(planId)}/outstanding-payments`
    )
    return response.data.data
  }

  /** Acceptance terms of every student in the account — pending banners on
   *  the plans list and the profile's signed history. */
  async listTerms(): Promise<TrainingCenterTermsItem[]> {
    const response = await this.get<{ data: TrainingCenterTermsItem[] }>('/me/training-center-terms')
    return response.data.data
  }

  async listCompletedLessons(
    id: string,
    params?: { limit?: number; cursor?: string }
  ): Promise<PaginatedResponse<CompletedLessonItem>> {
    const queryParams = new URLSearchParams()
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.cursor) queryParams.append('cursor', params.cursor)
    const suffix = queryParams.toString() ? `?${queryParams.toString()}` : ''

    const response = await this.get<PaginatedResponse<CompletedLessonItem>>(
      `/me/training-center-plans/${encodeURIComponent(id)}/completed-lessons${suffix}`
    )
    return response.data
  }
}

export const trainingCenterPlanService = new TrainingCenterPlanService()
